import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, StarIcon, PaperclipIcon, InboxIcon, SendIcon, ArchiveIcon, ReplyIcon, Trash2Icon } from 'lucide-react';
const mockEmails = [{
  id: 1,
  from: 'Board of Directors',
  initials: 'BD',
  color: '#C9A84C',
  subject: 'Q4 Strategic Review Meeting — Agenda Attached',
  preview: "Please find attached the agenda for tomorrow's board meeting...",
  time: '9:45 AM',
  unread: true,
  starred: true,
  hasAttachment: true
}, {
  id: 2,
  from: 'Sarah Mitchell — CFO',
  initials: 'SM',
  color: '#60A5FA',
  subject: 'Monthly Financial Summary — November 2024',
  preview: 'Consolidated financials for November show a 12% increase in EBITDA...',
  time: '8:30 AM',
  unread: true,
  starred: false,
  hasAttachment: true
}, {
  id: 3,
  from: 'Legal Department',
  initials: 'LD',
  color: '#A78BFA',
  subject: 'Contract Review: Partnership Agreement',
  preview: 'We have completed due diligence on the proposed partnership...',
  time: 'Yesterday',
  unread: false,
  starred: true,
  hasAttachment: false
}, {
  id: 4,
  from: 'Priya Sharma — EA',
  initials: 'PS',
  color: '#34D399',
  subject: "Tomorrow's Schedule Update",
  preview: 'Your 2 PM meeting has been rescheduled to 3:30 PM as requested...',
  time: 'Yesterday',
  unread: false,
  starred: false,
  hasAttachment: false
}, {
  id: 5,
  from: 'HR Director',
  initials: 'HR',
  color: '#F472B6',
  subject: 'Leadership Team Retreat — Final Details',
  preview: 'All arrangements for the upcoming leadership retreat have been finalized...',
  time: 'Dec 18',
  unread: false,
  starred: false,
  hasAttachment: true
}];
export function EmailWidget() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [emails, setEmails] = useState(mockEmails);
  const unreadCount = emails.filter(e => e.unread).length;
  const handleSelect = (id: number) => {
    setSelectedId(id === selectedId ? null : id);
    setEmails(prev => prev.map(e => e.id === id ? {
      ...e,
      unread: false
    } : e));
  };
  const toggleStar = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setEmails(prev => prev.map(em => em.id === id ? {
      ...em,
      starred: !em.starred
    } : em));
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.2
  }} className="glass-card rounded-2xl flex flex-col h-full overflow-hidden" style={{
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(201,168,76,0.2)'
  }} data-id="element-85">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[rgba(201,168,76,0.12)]" data-id="element-86">
        <div className="flex items-center gap-3" data-id="element-87">
          <div className="w-9 h-9 rounded-xl bg-[rgba(201,168,76,0.15)] flex items-center justify-center flex-shrink-0" data-id="element-88">
            <MailIcon className="w-4 h-4 text-[#C9A84C]" data-id="element-89" />
          </div>
          <div data-id="element-90">
            <h3 className="text-base font-semibold text-white" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-91">
              Inbox
            </h3>
            <p className="text-xs text-gray-400 mt-0.5" data-id="element-92">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-93">
          {[{
          icon: <InboxIcon className="w-4 h-4" data-id="element-94" />,
          label: 'Inbox'
        }, {
          icon: <SendIcon className="w-4 h-4" data-id="element-95" />,
          label: 'Sent'
        }, {
          icon: <ArchiveIcon className="w-4 h-4" data-id="element-96" />,
          label: 'Archive'
        }].map(({
          icon,
          label
        }) => <button key={label} title={label} className="w-9 h-9 rounded-lg bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.09)] flex items-center justify-center text-gray-400 hover:text-white transition-colors" data-id="element-97">
              {icon}
            </button>)}
        </div>
      </div>

      {/* Email List */}
      <div className="flex-1 overflow-y-auto" style={{
      maxHeight: '320px'
    }} data-id="element-98">
        {emails.map((email, index) => <motion.div key={email.id} initial={{
        opacity: 0,
        x: -12
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.06 * index
      }} data-id="element-99">
            {/* Email Row */}
            <button onClick={() => handleSelect(email.id)} className={`w-full text-left px-5 py-4 flex items-start gap-3 transition-all border-b border-[rgba(255,255,255,0.04)] ${selectedId === email.id ? 'bg-[rgba(201,168,76,0.08)]' : email.unread ? 'bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(255,255,255,0.03)]'}`} data-id="element-100">
              {/* Avatar */}
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{
            backgroundColor: `${email.color}25`,
            color: email.color
          }} data-id="element-101">
                {email.initials}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-102">
                <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-103">
                  <span className={`text-sm truncate ${email.unread ? 'font-semibold text-white' : 'font-medium text-gray-300'}`} data-id="element-104">
                    {email.from}
                  </span>
                  <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-105">
                    {email.hasAttachment && <PaperclipIcon className="w-3 h-3 text-gray-500" data-id="element-106" />}
                    <span className="text-xs text-gray-500 whitespace-nowrap" data-id="element-107">
                      {email.time}
                    </span>
                  </div>
                </div>
                <p className={`text-xs truncate ${email.unread ? 'text-gray-200' : 'text-gray-400'}`} data-id="element-108">
                  {email.subject}
                </p>
                <p className="text-xs text-gray-500 truncate mt-0.5" data-id="element-109">
                  {email.preview}
                </p>
              </div>

              {/* Unread dot + star */}
              <div className="flex flex-col items-center gap-2 flex-shrink-0 pt-1" data-id="element-110">
                {email.unread && <div className="w-2 h-2 rounded-full bg-[#C9A84C]" data-id="element-111" />}
                <button onClick={e => toggleStar(e, email.id)} className="text-gray-600 hover:text-[#C9A84C] transition-colors" data-id="element-112">
                  <StarIcon className={`w-3.5 h-3.5 ${email.starred ? 'text-[#C9A84C] fill-[#C9A84C]' : ''}`} data-id="element-113" />
                </button>
              </div>
            </button>

            {/* Expanded Email Actions */}
            {selectedId === email.id && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="px-5 py-3 bg-[rgba(201,168,76,0.05)] border-b border-[rgba(201,168,76,0.15)] flex gap-2" data-id="element-114">
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(201,168,76,0.15)] text-[#C9A84C] text-xs font-medium hover:bg-[rgba(201,168,76,0.25)] transition-colors" data-id="element-115">
                  <ReplyIcon className="w-3.5 h-3.5" data-id="element-116" /> Reply
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] text-gray-300 text-xs font-medium hover:bg-[rgba(255,255,255,0.1)] transition-colors" data-id="element-117">
                  <ArchiveIcon className="w-3.5 h-3.5" data-id="element-118" /> Archive
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(239,68,68,0.1)] text-red-400 text-xs font-medium hover:bg-[rgba(239,68,68,0.2)] transition-colors" data-id="element-119">
                  <Trash2Icon className="w-3.5 h-3.5" data-id="element-120" /> Delete
                </button>
              </motion.div>}
          </motion.div>)}
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-[rgba(201,168,76,0.12)]" data-id="element-121">
        <button className="w-full h-11 rounded-xl bg-[rgba(201,168,76,0.1)] hover:bg-[rgba(201,168,76,0.18)] text-[#C9A84C] text-sm font-medium transition-colors" data-id="element-122">
          View All in Outlook
        </button>
      </div>
    </motion.div>;
}